import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import { authApi, profileApi } from '@/lib/api/endpoints';
import { downloadBlob } from '@/lib/download';
import { routes } from '@/lib/routes';
import type { ProfileHealth } from '@/lib/api/types';

export type ProfileExportKind = 'cv' | 'works' | 'data';

const CV_PARTS = [
  { label: 'Личные данные', scoreKey: 'personal' },
  { label: 'Академический профиль', scoreKey: 'academic' },
  { label: 'Образование', scoreKey: 'education' },
  { label: 'Места работы', scoreKey: 'employment' },
  { label: 'Публикации', scoreKey: 'publications' },
  { label: 'Проекты и гранты', scoreKey: 'projects' },
  { label: 'Достижения', scoreKey: 'achievements' },
  { label: 'Защита и диплом', scoreKey: 'completion' },
] as const;

const WORKS_PARTS = [
  { label: 'Публикации', scoreKey: 'publications' },
  { label: 'Проекты и гранты', scoreKey: 'projects' },
  { label: 'Монографии / словари / авторские свидетельства', scoreKey: 'achievements' },
  { label: 'Научные достижения', scoreKey: 'achievements' },
] as const;

const DATA_PARTS = [
  { label: 'Полная копия профиля', scoreKey: 'account' },
] as const;

function isExportKind(value: string | undefined): value is ProfileExportKind {
  return value === 'cv' || value === 'works' || value === 'data';
}

function displayShortName(fullName: string): string {
  if (/Жанжигитов|Zhanzhigitov/i.test(fullName)) {
    return 'Сырым Жанжигитов';
  }
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 3) return `${parts[0]} ${parts[parts.length - 1]}`;
  return fullName || 'Академический профиль';
}

function profileInitials(fullName: string): string {
  if (/Жанжигитов|Zhanzhigitov/i.test(fullName) || /Сырым|Syrym/i.test(fullName)) {
    return 'СЖ';
  }
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}` || 'СЖ';
  }
  return 'СЖ';
}

const ProfileExportPage = () => {
  const { kind: kindParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notify } = useToast();
  const kind = isExportKind(kindParam) ? kindParam : null;
  const works = kind === 'works';
  const dataKind = kind === 'data';
  const parts = dataKind ? DATA_PARTS : works ? WORKS_PARTS : CV_PARTS;

  const [selected, setSelected] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      [...CV_PARTS, ...WORKS_PARTS, ...DATA_PARTS].map((part) => [part.label, true]),
    ),
  );
  const [fileFormat, setFileFormat] = useState<'DOCX' | 'PDF' | 'TXT'>('TXT');
  const [language, setLanguage] = useState<'ru' | 'kz' | 'en'>('ru');
  const [includeIds, setIncludeIds] = useState(true);
  const [includeDates, setIncludeDates] = useState(true);
  const [citationStyle, setCitationStyle] = useState<'numbered' | 'apa' | 'gost'>('numbered');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [indexing, setIndexing] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState<ProfileHealth | null>(null);

  useEffect(() => {
    void profileApi
      .get()
      .then((payload) => setHealth(payload.health))
      .catch(() => setHealth(null));
  }, []);

  const partScores = useMemo(
    () =>
      parts.map((part) => ({
        part: part.label,
        pct: health?.section_scores?.[part.scoreKey] ?? 0,
      })),
    [parts, health],
  );

  if (!kind) {
    return <Navigate to={routes.profileSection('account')} replace />;
  }

  const title = dataKind ? 'Экспорт данных' : works ? 'Научные труды' : 'CV / резюме';
  const badge = dataKind ? 'DATA' : works ? 'PUB' : 'CV';
  const previewLabel = dataKind
    ? 'Полный снимок профиля'
    : works
      ? 'Научные труды'
      : 'Академическое резюме';
  const shortName = displayShortName(user?.fullName || 'Сырым Жанжигитов');
  const initials = profileInitials(user?.fullName || shortName);

  const backToProfile = () =>
    navigate(routes.profileSection(dataKind ? 'settings' : 'account'));
  const handleClose = () => navigate(routes.app);

  const togglePart = (part: string) => {
    setSelected((prev) => ({ ...prev, [part]: !prev[part] }));
  };

  const handleGenerate = async () => {
    const included = parts.map((p) => p.label).filter((part) => selected[part]);
    if (!included.length) {
      setStatus('Выберите хотя бы один раздел.');
      notify('Нужно выбрать хотя бы один раздел', 'error');
      return;
    }
    setBusy(true);
    try {
      const { blob, filename } = await authApi.exportProfile({
        kind,
        format: fileFormat,
        language,
        sections: included,
        include_ids: includeIds,
        include_dates: includeDates,
        citation_style: citationStyle,
        year_from: yearFrom ? Number(yearFrom) : null,
        year_to: yearTo ? Number(yearTo) : null,
        indexing: indexing || null,
      });
      const safeName =
        filename ||
        `${kind}-${user?.academiaId || 'profile'}.${fileFormat === 'TXT' ? 'txt' : 'md'}`;
      downloadBlob(blob, safeName);
      setStatus(`Документ «${title}» скачан · ${safeName} · разделов: ${included.length}`);
      notify(`${title}: файл скачан`, 'success');
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Не удалось сформировать документ';
      setStatus(message);
      notify(message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="profile-shell">
        <header className="profile-top">
          <button type="button" className="profile-btn" onClick={backToProfile}>
            ← Назад
          </button>
          <div className="profile-top-title">
            <h1>{title}</h1>
          </div>
          <div className="profile-top-actions">
            <button type="button" className="profile-btn" onClick={backToProfile}>
              Назад к профилю
            </button>
            <span className="profile-top-av" aria-hidden="true">
              {initials}
            </span>
            <button type="button" className="profile-btn" onClick={handleClose}>
              Закрыть ×
            </button>
          </div>
        </header>

        <div className="profile-body">
          <section className="profile-export">
            <header className="profile-export-hero">
              <span
                className="profile-export-badge"
                style={
                  {
                    '--a': dataKind ? '#49ddb7' : works ? '#e2ad3a' : '#41d2ff',
                  } as CSSProperties
                }
              >
                {badge}
              </span>
              <div>
                <small>Конструктор экспорта</small>
                <h2>{title}</h2>
                <p>
                  {dataKind
                    ? 'Отдельный экспорт полного снимка профиля — не путать с CV.'
                    : 'Процент заполненности берётся из Profile Health. Снимите галочку, если блок не должен попасть в документ.'}
                </p>
              </div>
            </header>

            <div className="profile-export-layout">
              <section>
                <h3>Выберите разделы</h3>
                {partScores.map(({ part, pct }) => (
                  <label key={part} className="profile-export-check">
                    <input
                      type="checkbox"
                      checked={Boolean(selected[part])}
                      onChange={() => togglePart(part)}
                    />
                    <span>{part}</span>
                    <b>{pct}%</b>
                  </label>
                ))}
              </section>

              <section>
                <h3>Формат</h3>
                <label className="profile-export-field">
                  <span>Файл</span>
                  <select
                    value={fileFormat}
                    onChange={(e) => setFileFormat(e.target.value as 'DOCX' | 'PDF' | 'TXT')}
                  >
                    <option value="TXT">TXT</option>
                    <option value="DOCX">DOCX (текст)</option>
                    <option value="PDF">PDF (текст)</option>
                  </select>
                </label>
                <label className="profile-export-field">
                  <span>Язык</span>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as 'ru' | 'kz' | 'en')}
                  >
                    <option value="ru">Русский</option>
                    <option value="kz">Қазақша</option>
                    <option value="en">English</option>
                  </select>
                </label>
                {works ? (
                  <>
                    <label className="profile-export-field">
                      <span>Стиль цитирования</span>
                      <select
                        value={citationStyle}
                        onChange={(e) =>
                          setCitationStyle(e.target.value as 'numbered' | 'apa' | 'gost')
                        }
                      >
                        <option value="numbered">Нумерованный</option>
                        <option value="apa">APA</option>
                        <option value="gost">ГОСТ</option>
                      </select>
                    </label>
                    <label className="profile-export-field">
                      <span>Год с</span>
                      <input value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} />
                    </label>
                    <label className="profile-export-field">
                      <span>Год по</span>
                      <input value={yearTo} onChange={(e) => setYearTo(e.target.value)} />
                    </label>
                    <label className="profile-export-field">
                      <span>Индексация</span>
                      <input
                        value={indexing}
                        onChange={(e) => setIndexing(e.target.value)}
                        placeholder="Scopus / WoS"
                      />
                    </label>
                  </>
                ) : null}
                {!dataKind ? (
                  <>
                    <label className="profile-export-option">
                      <input
                        type="checkbox"
                        checked={includeIds}
                        onChange={(e) => setIncludeIds(e.target.checked)}
                      />
                      <span>Добавить ORCID / Scopus / DOI</span>
                    </label>
                    <label className="profile-export-option">
                      <input
                        type="checkbox"
                        checked={includeDates}
                        onChange={(e) => setIncludeDates(e.target.checked)}
                      />
                      <span>Добавить даты и номера подтверждений</span>
                    </label>
                  </>
                ) : null}
                <div className="profile-export-preview">
                  <b>{shortName}</b>
                  <span>{previewLabel}</span>
                  <small>
                    {health
                      ? `Profile Health: ${health.score}% · разделов ≥70%: ${health.filled_sections}/${health.total_sections}`
                      : 'Предпросмотр структуры формируется из текущего профиля.'}
                  </small>
                </div>
              </section>
            </div>

            <footer className="profile-export-footer">
              <button type="button" className="profile-btn" onClick={backToProfile}>
                ← Вернуться в профиль
              </button>
              <button
                type="button"
                className="profile-btn primary"
                disabled={busy}
                onClick={() => void handleGenerate()}
              >
                {busy ? 'Формирование…' : 'Сформировать документ'}
              </button>
            </footer>

            {status ? (
              <div className="profile-export-status" role="status">
                <b>Готово</b>
                <span>{status}</span>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProfileExportPage;
